import { FC } from 'react'

import {
  BankingPaymentFilter,
  PaymentsStatusWidgetDocument,
  PaymentsStatusWidgetQuery,
} from 'generated/resources/graphql'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

import {
  ChartData,
  FinanceWidget,
  SupportedCharts,
} from 'modules/finance/components/Widgets/common/FinanceWidget'
import { buildWidget } from 'modules/finance/components/Widgets/common/widgets'

interface PreparedDataArgsType {
  amountFormatter: (value: number) => string
  displayedFilters: (string | string[])[]
}

interface QueryArgsType {
  currency: string
  paymentsFilter?: BankingPaymentFilter
}

const prepareData = (
  data: PaymentsStatusWidgetQuery,
  prepareDataArgs: PreparedDataArgsType
) => {
  const paidAmount = data?.banking?.paid?.aggregate?.sum?.amountCents || 0
  const paidCount = data?.banking?.paid?.aggregate?.count || 0
  const inProgressAmount = data?.banking?.inProgress?.aggregate?.sum?.amountCents || 0
  const inProgressCount = data?.banking?.inProgress?.aggregate?.count || 0
  const requiresAttentionAmount =
    data?.banking?.requiresAttention?.aggregate?.sum?.amountCents || 0
  const requiresAttentionCount = data?.banking?.requiresAttention?.aggregate?.count || 0
  const totalAmount = paidAmount + inProgressAmount + requiresAttentionAmount
  const totalCount = paidCount + inProgressCount + requiresAttentionCount

  const groupedChartData: ChartData[] = [
    {
      status: 'paid',
      color: 'feedback-positive',
      label: `${i18n.t('banking.treasury.insights.paid')} (${paidCount})`,
      value: paidAmount,
      formattedValue: prepareDataArgs.amountFormatter(paidAmount),
    },
    {
      status: 'inProgress',
      color: 'categorical-2',
      label: `${i18n.t('banking.treasury.insights.inProgress')} (${inProgressCount})`,
      value: inProgressAmount,
      formattedValue: prepareDataArgs.amountFormatter(inProgressAmount),
    },
    {
      status: 'requiresAttention',
      color: 'feedback-negative',
      label: `${i18n.t('finance.overview.payments.requires_attention')} (${requiresAttentionCount})`,
      value: requiresAttentionAmount,
      formattedValue: prepareDataArgs.amountFormatter(requiresAttentionAmount),
    },
  ]

  const overviewChartData: ChartData = {
    label: `${i18n.t('banking.treasury.insights.total')} (${totalCount})`,
    value: totalAmount,
    formattedValue: prepareDataArgs.amountFormatter(totalAmount),
  }

  return {
    overviewChartData,
    groupedChartData,
    amountFormatter: prepareDataArgs.amountFormatter,
    displayedFilters: prepareDataArgs.displayedFilters,
  }
}

export const buildPaymentsStatusDashboardWidget = ({
  queryArgs,
  prepareDataArgs,
}: {
  queryArgs: QueryArgsType
  prepareDataArgs: PreparedDataArgsType
}) => {
  const PaymentsStatusDashboardWidget: FC<{
    data: ReturnType<typeof prepareData>
  }> = ({ data }) => {
    const { navigation } = useNavigation()
    const { overviewChartData, groupedChartData, amountFormatter, displayedFilters } = data

    return (
      <FinanceWidget
        id="paymentsStatusDashboard"
        title={i18n.t('finance.overview.payments.payments_by_state')}
        displayedFilters={displayedFilters}
        tickFormatter={(value) => amountFormatter(Number(value))}
        type={SupportedCharts.PIE}
        showChart
        overviewChartData={overviewChartData}
        chartDatas={groupedChartData}
        linkData={{
          url: `${navigation.finance.banking.payments.fullPath()}?status=pending`,
          title: i18n.t('banking.payments.short_title'),
        }}
        emptyState={{
          icon: '💰',
          text: i18n.t('finance.overview.payments.empty_state_by_status.title'),
          description: i18n.t('finance.overview.payments.empty_state_by_status.description'),
        }}
      />
    )
  }

  return buildWidget({
    id: 'paymentsStatusWidget',
    component: PaymentsStatusDashboardWidget,
    document: PaymentsStatusWidgetDocument,
    prepareData: (data: PaymentsStatusWidgetQuery) => prepareData(data, prepareDataArgs),
    queryArgs: queryArgs,
  })
}
